import React from "react";
import type { ReactNode } from "react";
import { View, Text, StyleSheet, Image } from "@react-pdf/renderer";
import logoHorizontal from "../assets/Logo MIMAP Horizontal second--02.png";
import MIMAPICONE from "../assets/MIMAP ICONE.png";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    position: "relative",
  },
  container: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#113285",
    borderBottomStyle: "solid",
  },
  logo: {
    width: 200,
    height: 70,
  },
  headerText: {
    textAlign: "right",
  },
  title: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 3,
  },
  documentHeader: {
    marginTop: 3,
    marginBottom: 8,
    alignItems: "center",
  },
  documentTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 2,
    textTransform: "uppercase",
  },
  documentSubtitle: {
    fontSize: 9,
    color: "#6b7280",
    marginBottom: 1,
  },
  content: {
    flex: 1,
    marginBottom: 5,
  },
  footer: {
    marginTop: 8,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    borderTopStyle: "solid",
    textAlign: "center",
    fontSize: 7,
    color: "#6b7280",
  },
  watermark: {
    position: "absolute",
    opacity: 0.08,
    width: 250,
    height: 250,
    textAlign: "center",
    left: 30,
    top: 180,
  },
});

interface StandardPDFLayoutProps {
  title: string;
  children: ReactNode;
  documentNumber?: string;
  documentDate: string;
  showWatermark?: boolean;
  pageSize?: "A4" | "A5";
}

const StandardPDFLayout: React.FC<StandardPDFLayoutProps> = ({
  title,
  children,
  documentNumber,
  documentDate,
  showWatermark = false,
  pageSize = "A4",
}) => {
  const isA5 = pageSize === "A5";
  const dynamicStyles = {
    pagePadding: isA5 ? 15 : 30,
    logoWidth: isA5 ? 120 : 200,
    logoHeight: isA5 ? 42 : 70,
    watermarkLeft: isA5 ? 15 : 30,
    watermarkTop: isA5 ? 120 : 180,
    documentTitleSize: isA5 ? 11 : 14,
  };
  return (
    <View style={styles.container}>
      {showWatermark && (
        <Image
          style={[
            styles.watermark,
            {
              left: dynamicStyles.watermarkLeft,
              top: dynamicStyles.watermarkTop,
            },
          ]}
          src={MIMAPICONE}
          fixed
        />
      )}

      {/* Header with logo */}
      <View style={styles.header}>
        <Image
          style={[
            styles.logo,
            {
              width: dynamicStyles.logoWidth,
              height: dynamicStyles.logoHeight,
            },
          ]}
          src={logoHorizontal}
        />
        <View style={styles.headerText}>
          <Text style={styles.title}>Votre santé, notre priorité</Text>
        </View>
      </View>

      {/* Document header */}
      <View style={styles.documentHeader}>
        <Text
          style={[
            styles.documentTitle,
            { fontSize: dynamicStyles.documentTitleSize },
          ]}
        >
          {title}
        </Text>
        {documentNumber && (
          <Text style={styles.documentSubtitle}>N° {documentNumber}</Text>
        )}
        <Text style={styles.documentSubtitle}>
          {(() => {
            const parsed = new Date(documentDate);
            const isValid = !Number.isNaN(parsed.getTime());
            if (isValid) {
              return `Établi le ${parsed.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}`;
            }
            // If the provided documentDate is already a human-readable string
            // (eg. "mercredi 24 décembre 2025") or otherwise not parseable,
            // render it as-is to avoid `Invalid Date`.
            return `Établi le ${documentDate}`;
          })()}
        </Text>
      </View>

      {/* Main content */}
      <View style={styles.content}>{children}</View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text>Ilot k, Ext 929A Cite Plage -- Nouakchott</Text>
        <Text>Tel: 00-222-31-24-44-04 • Email: cabinetmimap@gmail.com</Text>
      </View>
    </View>
  );
};

export default StandardPDFLayout;
